import React, { useState } from 'react';
import { Handle, Position, useConnection, type Node, type NodeProps } from '@xyflow/react';
import { NODE_COLORS, NODE_DIMENSIONS, NODE_HIGHLIGHT_PULSE } from './skillTreeStyles';

export type SkillNodeData = {
    name: string;
    hasLinkedGuide: boolean;
    isSelected: boolean;
    isDevelopment: boolean;
    /** Present while an animation script pulses this node; token remounts the overlay so the animation restarts. */
    highlight?: { color: string; token: number };
};

export type SkillFlowNode = Node<SkillNodeData, 'skill'>;

export const SkillNodeView: React.FC<NodeProps<SkillFlowNode>> = ({ id, data }) => {
    const [isHovered, setIsHovered] = useState(false);
    const connection = useConnection();
    const isConnectionTarget =
        connection.inProgress && connection.toNode?.id === id && connection.fromNode?.id !== id;

    const fill = data.isSelected
        ? NODE_COLORS.selected
        : isHovered
            ? NODE_COLORS.hover
            : data.hasLinkedGuide
                ? NODE_COLORS.default
                : NODE_COLORS.noLinkedGuide;
    const stroke = isConnectionTarget
        ? NODE_COLORS.dragTarget
        : data.isSelected
            ? NODE_COLORS.stroke.selected
            : NODE_COLORS.stroke.default;
    const strokeWidth = isConnectionTarget || data.isSelected ? 4 : 2;

    return (
        <div
            className="node"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                width: NODE_DIMENSIONS.width,
                height: NODE_DIMENSIONS.height,
                boxSizing: 'border-box',
                borderRadius: 8,
                background: fill,
                border: `${strokeWidth}px solid ${stroke}`,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                transition: 'background 0.2s ease-in-out', // Smooth transition for color changes
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                fontSize: 12,
                color: '#000',
                cursor: 'pointer',
            }}
        >
            <span className="node-text">{data.name}</span>
            {data.highlight && (
                <div
                    key={data.highlight.token}
                    className="skill-node-highlight-pulse"
                    style={{
                        boxShadow: `0 0 18px 10px ${data.highlight.color}`,
                        animation: `skill-node-highlight-pulse ${NODE_HIGHLIGHT_PULSE.durationMs}ms ease-in-out ${NODE_HIGHLIGHT_PULSE.iterations}`,
                    }}
                />
            )}
            {/*
             * Edges anchor at a handle's OUTER edge (xyflow getHandlePosition), so any handle
             * that straddles the node border pushes the edge endpoint off the node by half its
             * size. Keep the measured handle boxes 1px and flush; the visible orange grab
             * circle is a child (children don't affect measurement, clicks bubble to the handle).
             */}
            <Handle
                type="target"
                position={Position.Top}
                isConnectable={data.isDevelopment}
                style={{
                    width: 1,
                    height: 1,
                    minWidth: 0,
                    minHeight: 0,
                    border: 'none',
                    background: 'transparent',
                    opacity: 0,
                    pointerEvents: 'none',
                }}
            />
            {/* Link-handle: drag from here to another node to create a link (dev only) */}
            <Handle
                type="source"
                position={Position.Bottom}
                className="link-handle"
                isConnectable={data.isDevelopment}
                style={{
                    width: 1,
                    height: 1,
                    minWidth: 0,
                    minHeight: 0,
                    border: 'none',
                    background: 'transparent',
                    overflow: 'visible',
                    cursor: 'crosshair',
                    pointerEvents: data.isDevelopment ? 'all' : 'none',
                }}
            >
                <span
                    style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: '#ff9900',
                        border: '1px solid #000',
                        cursor: 'crosshair',
                        opacity: data.isDevelopment && isHovered ? 1 : 0,
                        pointerEvents: data.isDevelopment ? 'all' : 'none',
                    }}
                />
            </Handle>
        </div>
    );
};
